import { useCallback, useEffect, useState } from "react";
import { getHaryanaMonthlyProcurementData, getHaryanaYearlyProcurementData } from "../../../Rest_api/RestApi";
import { HaryanaMonthlyProcurementData, HaryanaYearlyProcurementData } from "../../../models/haryana";
import FilterMenu from "../../../components/FilterMenu";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { ChartType, LegendKey } from "../../../models/chart_model";
import { titleCase } from "../../../extensions/extension";
import { CHART_TYPES } from "../../Dashboard/Gas/Gas_Const";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
  LegendSummaryTableProps,
} from "../../../components/table/SummaryTable";
import { ChartAreaLoader } from "../../../components/Loading";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";

export const VOLUME_KEYS: string[] = [
  "quantum_exchange",
  "quantum_long_term",
  "quantum_medium_term",
  "quantum_short_term",
];

export const PRICE_KEYS: string[] = [
  "appc_exchange",
  "appc_long_term",
  "appc_medium_term",
  "appc_short_term",
];

export const COST_KEYS: string[] = [
  "total_cost_exchange",
  "total_cost_long_term",
  "total_cost_medium_term",
  "total_cost_short_term",
];

export const TOTAL_VOL_KEYS: string[] = ["quantum_total_purchase"];

export const TOTAL_PRICE_KEYS: string[] = ["appc_total_purchase"];

export const TOTAL_COST_KEYS: string[] = ["total_cost_total_purchase"];

const Interval = ["Month", "Year"];

const Y_AXIS = [
  { label: "Price", values: PRICE_KEYS, unit: "Rs/KWh" },
  { label: "Cost", values: COST_KEYS, unit: "Rs Cr." },
];

const GROUP_BY = [
  {
    label: "Product",
    cost: COST_KEYS,
    volume: VOLUME_KEYS,
    unit: "MUs",
    price: PRICE_KEYS,
  },
  {
    label: "Total",
    cost: TOTAL_COST_KEYS,
    volume: TOTAL_VOL_KEYS,
    unit: "MUs",
    price: TOTAL_PRICE_KEYS,
  },
];

export default function Appc() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<HaryanaMonthlyProcurementData[] | HaryanaYearlyProcurementData[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Interval[0]);
  const [selectedYAxis, setSelectedYAxis] = useState<{
    label: string;
    values: string[];
    unit: string;
  }>(Y_AXIS[0]);
  const [selectedChartType, setSelectedChartType] = useState(CHART_TYPES[1]);
  const [selectedGroupBy, setSelectedGroupBy] = useState(GROUP_BY[0]);

  const _handlefetchData = useCallback(async () => {
    if (selectedInterval === "Month") {
      const res = await getHaryanaMonthlyProcurementData();
      setData(res);
    } else {
      const res = await getHaryanaYearlyProcurementData();
      setData(res);
    }
    setLoading(false);
  }, [selectedInterval]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const tablePar: LegendSummaryTableProps = {
    legends: selectedGroupBy.volume.map((item) => ({
      dataKey: item,
      name: titleCase(item.replaceAll("_", " ")),
      unit: "MUs",
      type: selectedChartType.value,
      stackId: selectedChartType.stackId,
    })),
    priceLegends: selectedYAxis.values.map((item) => ({
      dataKey: item,
      name: titleCase(item.replaceAll("_", " ")),
      unit: selectedYAxis.unit,
      type: selectedChartType.value,
      stackId: selectedChartType.stackId,
    })),
    data: data,
    unit: "mus",
    extraKeys: selectedInterval === "Month" ? ["month", "year"] : ["year"],
    extraLabels: selectedInterval === "Month" ? ["Month", "Year"] : ["Year"],
    filename: "Procurement month",
  };
  const chartTablePar: LegendSummaryTableProps = {
    legends: getLegends(),
    data: data,
    extraKeys: selectedInterval === "Month" ? ["month", "year"] : ["year"],
    extraLabels: selectedInterval === "Month" ? ["Month", "Year"] : ["Year"],
    filename: "Procurement month",
  };

  function getLegends(): LegendKey[] {
    const volumeLegends: LegendKey[] = selectedGroupBy.volume.map(
      (item, index) => ({
        dataKey: item,
        name: titleCase(item.replaceAll("_", " ")),
        unit: "MUs",
        stroke: ExchangeColors[index],
        type: selectedChartType.value,
        stackId: selectedChartType.stackId,
      })
    );

    const priceLegends: LegendKey[] = (
      selectedYAxis.label == "Cost"
        ? selectedGroupBy.cost
        : selectedGroupBy.price
    ).map((item, index) => ({
      dataKey: item,
      name: titleCase(item.replaceAll("_", " ")),
      yAxisId: "right",
      stroke: lightenColor(ExchangeColors[index], 20),
      unit: selectedYAxis.unit,
      type: ChartType.Line,
    }));
    return [...volumeLegends, ...priceLegends];
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Interval}
            selectedItems={[selectedInterval]}
            onSelect={(item, index) => {
              setSelectedInterval(Interval[index]);
            }}
          />
          <FilterMenu
            title="Y-Axis"
            items={Y_AXIS.map((item) => item.label)}
            selectedItems={[selectedYAxis.label]}
            onSelect={(item, index) => {
              setSelectedYAxis(Y_AXIS[index]);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(CHART_TYPES[index]);
            }}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={[selectedGroupBy.label]}
            onSelect={(item, index) => {
              setSelectedGroupBy(GROUP_BY[index]);
            }}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading APPC" subtitle="Fetching HPPC procurement data…" /> : (<>
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-80">Procurement ({selectedInterval}-Wise)</h2>
            <button
              onClick={() => {
                dataTab.label === "Chart" &&
                  downloadChartTableCSV(chartTablePar);

                dataTab.label === "Summary" && downloadSummaryCSV(tablePar);

                dataTab.label === "Table" &&
                  downloadChartTableCSV(chartTablePar);
              }}
              className="downloadButton"
            >
              Download Data
            </button>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={data as any}
              showBrush={data.length > 12}
              brushIndex={{
                startIndex: data.length - 13,
                endIndex: data.length - 1,
              }}
              xDataKey={selectedInterval === "Month" ? "month" : "year"}
              yAxisLabel="Volume (MUs)"
              secondYAxisLabel={
                selectedYAxis.label + " (" + selectedYAxis.unit + ")"
              }
              legends={getLegends()}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...chartTablePar} />}
          </>)}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<div> HPPC : Exchange Sell Purchase</div>}
              text="1.	Comparative gives product and exchange segmentation. 
              "
            >
              <p className="gna-footer-text"> Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}